import {ApiClient} from '@hconnect/apiclient'
import {useQuery, UseQueryOptions, useQueryClient} from '@tanstack/react-query'
import {AxiosError, AxiosInstance} from 'axios'
import moment, {MomentInput} from 'moment-timezone'
import {useEffect} from 'react'

import {mapRunningTimesOffPeriodsWithoutDetailFlatList} from '../../mappers'
import {RunningTimesOffPeriodsWithoutDetail, ThinEquipmentRunningTimes} from '../../types'

type Params = {
  from: string
  to: string
}

export const RunningTimesOffPeriodsQueryKey = 'running-times-off-periods-without-detail'

const getBasePath = (plantId: string) =>
  `/downtime-dashboard/${plantId}/running-times/off-periods-without-detail`

export const fetchRunningTimesOffPeriodsWithoutDetail = async ({
  plantId,
  axiosInstance,
  params,
  signal
}: {
  plantId: string
  axiosInstance: AxiosInstance
  params: Params
  signal?: AbortSignal
}) => {
  const path = getBasePath(plantId)
  const response = await axiosInstance.get<ThinEquipmentRunningTimes[]>(path, {
    params,
    signal
  })

  return response.data
}

export const useRunningTimesOffPeriodsWithoutDetail = (
  plantId: string,
  params: Params,
  setData?: (data: RunningTimesOffPeriodsWithoutDetail[]) => void,
  options?: Omit<
    UseQueryOptions<ThinEquipmentRunningTimes[], AxiosError, RunningTimesOffPeriodsWithoutDetail[]>,
    'queryKey' | 'queryFn'
  > & {
    onError?: (error: AxiosError) => void
  }
) => {
  const onError = options?.onError

  const query = useQuery<
    ThinEquipmentRunningTimes[],
    AxiosError,
    RunningTimesOffPeriodsWithoutDetail[]
  >({
    queryKey: [RunningTimesOffPeriodsQueryKey, plantId, params.from, params.to],
    queryFn: async ({signal}) =>
      fetchRunningTimesOffPeriodsWithoutDetail({
        plantId,
        axiosInstance: ApiClient.axiosInstance,
        params,
        signal
      }),
    staleTime: 1000 * 60 * 15,
    select: (data) => {
      const transformedData: RunningTimesOffPeriodsWithoutDetail[] =
        mapRunningTimesOffPeriodsWithoutDetailFlatList(data).sort(
          ({runningTime: {beginTechnical: a}}, {runningTime: {beginTechnical: b}}) =>
            moment(b as MomentInput).diff(a as MomentInput)
        )
      setData?.(transformedData)
      return transformedData
    },
    ...options
  })

  useEffect(() => {
    if (query.error) {
      onError?.(query.error)
    }
  }, [query.error, onError])

  return query
}

export const useRunningTimesOffPeriodsWithoutDetailCount = (
  plantId: string,
  params: Params,
  options?: Omit<UseQueryOptions<number, AxiosError>, 'queryKey' | 'queryFn'> & {
    onError?: (error: AxiosError) => void
  }
) => {
  const queryClient = useQueryClient()
  const onError = options?.onError

  const query = useQuery<number, AxiosError>({
    queryKey: [`${RunningTimesOffPeriodsQueryKey}/count`, plantId, params.from, params.to],
    queryFn: async ({signal}) => {
      const path = `${getBasePath(plantId)}/count`
      const response = await ApiClient.axiosInstance.get<number>(path, {
        params,
        signal
      })

      return response.data
    },
    staleTime: 1000 * 60 * 15,
    ...options
  })

  useEffect(() => {
    if (query.error) {
      onError?.(query.error)
    }
  }, [query.error, onError])

  useEffect(() => {
    if (query.isSuccess && query.data > 0) {
      // this query is fetched with useRunningTimesOffPeriodsWithoutDetail
      void queryClient.prefetchQuery({
        queryKey: [RunningTimesOffPeriodsQueryKey, plantId, params.from, params.to],
        queryFn: ({signal}) =>
          fetchRunningTimesOffPeriodsWithoutDetail({
            plantId,
            axiosInstance: ApiClient.axiosInstance,
            params: {
              from: params.from,
              to: params.to
            },
            signal
          })
      })
    }
  }, [query.isSuccess, query.data, queryClient, plantId, params.from, params.to])

  return query
}
